import type { ReactElement } from "react";

/**
 * The home menu's glyphs, engraved rather than drawn on.
 *
 * Each one is a stroke-only 24x24 shape set painted twice: a white copy one
 * unit low, then a black copy at rest on top. With light coming from above,
 * that pair reads as a groove cut into the button face rather than a mark
 * sitting on it.
 *
 * Both passes are plain black/white alphas rather than theme colors, which is
 * what lets one icon set sit on every venue — the pale linen buttons, the dark
 * walnut ones, and the brass primary plaque all shade the same way. Anything
 * keyed to `--ink` would go light-on-light or dark-on-dark as venues change.
 *
 * The shapes share a vocabulary on purpose: a rounded rectangle is a card or a
 * screen, and a circle is a player.
 */

export type MenuIconName = "resume" | "quick" | "campaign" | "local" | "online" | "themes" | "progress" | "rules" | "rulebook" | "tutorial" | "settings";

const GLYPHS: Record<MenuIconName, ReactElement> = {
  // A round trip back to where the deck was left.
  resume: (
    <>
      <path d="M19.29 10.72A7.4 7.4 0 1 1 15.7 5.59" />
      <path d="M12.29 5.7 15.7 5.59 14.09 2.58" />
    </>
  ),
  // One card, dealt at speed: straight to a table, nothing to set up.
  quick: (
    <>
      <rect x="4.6" y="4.4" width="9.6" height="14.4" rx="2" />
      <path d="M17.4 4.8 15.3 11h4.1l-3 8.2" />
    </>
  ),
  // A winding road up to a flag: somewhere to get to, a step at a time.
  campaign: (
    <>
      <path d="M3.4 20.6c3.6 0 4.4-3.2 7.4-3.2s3.3 2.4 5.6.2c1.9-1.8-.6-4.4-2.8-5.4" />
      <circle cx="13" cy="11.6" r="0.6" />
      <path d="M15.4 11.4V2.6l5.2 2.2-5.2 2.2" />
    </>
  ),
  // Players gathered around the one device that is passed between them.
  local: (
    <>
      <rect x="8.7" y="5.8" width="6.6" height="9.6" rx="2" />
      <circle cx="4.2" cy="9.4" r="1.95" />
      <circle cx="19.8" cy="9.4" r="1.95" />
      <circle cx="12" cy="19.4" r="1.95" />
    </>
  ),
  // Separate devices, joined by something you cannot see.
  online: (
    <>
      <rect x="8.3" y="2.4" width="7.4" height="5.4" rx="1.7" />
      <rect x="1.2" y="16" width="7.4" height="5.4" rx="1.7" />
      <rect x="15.4" y="16" width="7.4" height="5.4" rx="1.7" />
      {/* A near-zero dash with round caps draws as dots, not dashes. */}
      <g strokeDasharray="0.1 2.3">
        <path d="M10.4 8.4 6.4 15.4" />
        <path d="M13.6 8.4 17.6 15.4" />
        <path d="M9.1 18.7h5.8" />
      </g>
    </>
  ),
  // Two cards fanned, the front one carrying a color. The back card is kept
  // clear of the dot: an edge crossing it turns the swatch into a notch.
  themes: (
    <>
      <rect x="3.1" y="5.2" width="9" height="12.8" rx="2.1" transform="rotate(-15 7.6 11.6)" />
      <rect x="11.2" y="6.2" width="9.6" height="13.4" rx="2.2" />
      <circle cx="16" cy="12.9" r="1.6" data-solid="" />
    </>
  ),
  // Bars climbing towards the next level.
  progress: (
    <>
      <rect x="3.7" y="13.6" width="4.6" height="7" rx="1.5" />
      <rect x="9.7" y="9.4" width="4.6" height="11.2" rx="1.5" />
      <rect x="15.7" y="4.6" width="4.6" height="16" rx="1.5" />
    </>
  ),
  // A card with a question on it.
  rules: (
    <>
      <rect x="5.4" y="3.4" width="13.2" height="17.2" rx="2.6" />
      <path d="M9.45 9.2A2.55 2.55 0 1 1 12 11.75v2.55" />
      <circle cx="12" cy="16.9" r="1" data-solid="" />
    </>
  ),
  // The same card with the answer written out on it: the house rules, in full.
  rulebook: (
    <>
      <rect x="5.4" y="3.4" width="13.2" height="17.2" rx="2.6" />
      <path d="M8.7 8.5h6.6" />
      <path d="M8.7 12h6.6" />
      <path d="M8.7 15.5h4.1" />
    </>
  ),
  // A card being played for you — the same shape, with a solid play mark on it.
  tutorial: (
    <>
      <rect x="5.4" y="3.4" width="13.2" height="17.2" rx="2.6" />
      <path d="M10.3 8.5 15.7 12l-5.4 3.5z" data-solid="" />
    </>
  ),
  // A cog: the settings. Drawn a little inside the box, because the engraving's
  // light pass sits one unit low and a full-size cog would lose its bottom tooth.
  settings: (
    <g transform="translate(1.56 1.2) scale(0.87)">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </g>
  ),
};

export function MenuIcon({ name }: { name: MenuIconName }) {
  const glyph = GLYPHS[name];
  return (
    <svg className="menu-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g className="engrave-light" transform="translate(0 1)">
        {glyph}
      </g>
      <g className="engrave-shadow">{glyph}</g>
    </svg>
  );
}
